import React from "react";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

const bps = DtvTheme.breakpoints;

import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvText from "../../../video-lab/dtv-text";
import Divider from "@directv-digital/fusion.molecules.divider";
import Link from "@directv-digital/fusion.atoms.link";
import Typography from "@mui/material/Typography";
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import { IPackage } from "@/ChnlListingPageV2/chnlListingPage.props";
import { zIndexMap } from "@/ChnlListingPageV2/utils/zIndexMap";
import { tooltipStyles } from "../IconBanner.styles";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

const MYHOMETEAMCTA_ENDICON_SRC = "https://www.directv.com/dtvassets/global/icons/2022/arrow-right_dk-bg.svg";

const LocalRsnTooltip = ({ type, tooltipProps, genreNationalLabel = '' }) => {
  // The badge label is intrinsic to the channel's classification (Local/RSN/National),
  // not to the CMS hover copy — so it must render whenever a local/RSN/national channel
  // is shown, even if the tooltip content is empty. Gating the whole badge on
  // `tooltipProps?.content` made the badge silently vanish whenever the
  // localAndRegionalChannels.localTooltip / rsnTooltip CMS field was empty (the
  // flag-ON channels-service story divergence from prod). The hover Tooltip wrapper is
  // applied only when there is content; otherwise the bare label renders.
  const hasTooltipContent = Boolean(tooltipProps?.content);
  const badge = (
    <Typography variant="body2" sx={{
      // eslint-disable-next-line eqeqeq
      width: type == "national" ? "66px" : "53px",
borderRadius: "4px",
      color: "#ffff",
backgroundColor: "rgb(85, 85, 85)",
      padding: "2px",
margin: "0px auto",
      fontSize: "12px !important",
letterSpacing: "-0.02em",
      cursor: hasTooltipContent ? 'pointer' : 'default'
    }}>
      {type === 'national' ? genreNationalLabel :  type === 'local' ? 'Local' : 'RSN'} ⓘ
    </Typography>
  );

  return (
    <DtvBox component="span" sx={{
      textAlign: 'center',
      width: 'auto',
      position: 'absolute',
      left: '50%',
bottom: "0px",
transform: 'translateX(-50%)',
      zIndex: zIndexMap.ctgBannerLocalRsnTooltipCtnr
    }}>
      {hasTooltipContent ? (
        <Tooltip
          describeChild
          content={tooltipProps.content}
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          placement="bottom"
          PopperProps={{
            sx: {
              "& .MuiTypography-root": {
                fontFamily: `roboto-regular`,
                fontSize: "14px"
              }
            }
          }}
          sx={{
            marginTop: "-20px"
          }}
          tooltipStyles={{
            backgroundColor: `${colorPalette.white.main} !important`,
            color: `${colorPalette.jet.main} !important`
          }}
          >
            {badge}
        </Tooltip>
      ) : badge}
    </DtvBox>
  );
};

const ShowMoreButton = ({ showMoreKey, ctgIndex, ctgKey, handleShowMoreClick, mode, textColor }) => (
  <DtvBox
    id={`chnlCtg-show-more-${ctgIndex}`}
    sx={{
      display: "block",
      visibility: "hidden", // initially hide from DOM
      pointerEvents: {
        xs: "auto",
        sm: "none"
      },
      textAlign: "center",
      margin: "12px 0 12px",
      position: "relative"
    }}>
    <DtvButton
      ariaLabel={!showMoreKey ? 'Show more channels' : 'Show Less channels'}
      label={!showMoreKey ? 'Show More' : 'Show Less'}
      variant= "uxtext"
      endIcon={
        <CardMedia
          alt={!showMoreKey ? "Show More channels icon" : "Show Less channels icon"}
          component="img"
          loading="lazy"
          src={"https://www.directv.com/dtvassets/global/icons/2022/arrow_down_gray700.svg"}
          sx={{
            height: "16px",
            width: "16px",
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
              // Tanner: svg coloring - https://stackoverflow.com/questions/22252472/how-can-i-change-the-color-of-an-svg-element
          }}
        />
      }
      sx={{
        "& img": {
          transform: !showMoreKey ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 200ms linear"
        },
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "18.2px",
        width: "120px",
        height: "36px",
        textDecoration: "none !important",
        color: textColor,
        transition: "all ease 200ms",
        "&:focus": {
          outline: "4px solid #1FBCF8",
          color: textColor
        },
        "&:hover": {
          backgroundColor: "#FFF", // #3885E1
          color: mode === "dark" ? colorPalette.jet.main : textColor,
          "& img": {
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(10%) sepia(68%) saturate(1229%) hue-rotate(186deg) brightness(95%) contrast(93%) !important" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        },
        "&:active": {
          backgroundColor: "#1FBCF83D",
          color: textColor,
          "& img": {
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        }
      }}
      onClick={evt => {
        evt?.target?.blur();
        handleShowMoreClick(ctgKey);
      }}
    />
  </DtvBox>
);

const CategoryHeaderCta = ({ mode, ctaLabel, ctaUrl }) => (
  <Link
    subtleEmphasis
    ariaLabel={ctaLabel}
    href={ctaUrl}
    mode={mode}
    variant={"uxlinkicon"}
    {...getAnalyticsAttrs(ctaLabel?.toLowerCase(), "body", ctaUrl)}
    sx={{
      minWidth: "fit-content",
      height: "20.8px !important",
      margin: "12px 0px 4px 26px !important",
      fontSize: "14px !important",
      // match 800px flex change on header
      [bps.up(800)]: {
        fontSize: "16px !important",
        margin: "0px 0px 10px 26px !important"
      }
    }}
    onClick={() => {
      window.location.href = ctaUrl;
    }}
  >{ctaLabel}</Link>
);

const CategoryFooterCta = ({ mode, footerCta }) => (
  <DtvButton
    ariaLabel={footerCta?.label}
    googleAnalyticsData={getAnalyticsAttrs(footerCta?.label?.toLowerCase(), "body", footerCta?.targeturl)}
    label={footerCta?.label}
    modalData={{ url: footerCta?.targeturl }}
    modalType={"iframe"}
    mode={mode === "dark" ? "light" : "dark"}
    variant={"uxtext"}
    endIcon={
      <img
        alt={footerCta?.label}
        loading="lazy"
        src={MYHOMETEAMCTA_ENDICON_SRC}
        style={{
          height: "16px",
          ...mode === "light" && {
            filter: "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        }}
      />
    }
    sx={{
      mt: { xs: "10px" },
      ml: {
 xs: "16px",
sm: "0px"
},
      marginBottom: {
 xs: "5.85px",
sm: "0px"
},
      paddingTop: {
 xs: "5px",
sm: "0px"
},
      paddingBottom: {
 xs: "7px",
sm: "5px"
},
      textDecoration: "none !important",
      alignSelf: { xs: 'flex-start'  },
      fontSize: {
 sm: "14px !important",
md: "16px !important"
},
      '& .MuiButton-endIcon, .DtvButton-endIcon': {
        display: 'flex',
        alignSelf: 'end'
      },
      "&:hover,&:focus,&:active": {
        backgroundColor: "unset",
        textDecoration: "underline !important"
      }
    }}
  />
);

const MockIconBg = () => (
  <svg viewBox="0 0 261 261" xmlns="http://www.w3.org/2000/svg" style={{
    position: 'absolute',
top: 0,
left: 0,
right: 0,
bottom: 0,
    paddingTop: "2px",
    zIndex: zIndexMap.channelIconBackground,
    transform: "scale(0.9)"
  }}>
    <g><circle cx="108" cy="108" data-name="Ellipse 34" fill="#747474" r="108" transform="translate(22.5 20.5)"/></g>
  </svg>
);

const IconCategory = ({
  channels,
  ctgKey,
  ctgValue,
  ctgIndex,
  isLast,
  packageData,
  showMoreKey,
  handleShowMoreClick,
  handleIconClick,
  footerCta,
  zipCodeModuleProps,
  localChnlBadgeTooltip,
  rsnChnlBadgeTooltip,
  theming,
  genreNationals
}) => {
  const iconUrl = (packageData as IPackage)?.icon || "";
  const ctaUrl = (packageData as IPackage)?.gpCtaProps?.targeturl || "";
  const ctaLabel = (packageData as IPackage)?.gpCtaProps?.label || "";
  const ctgHeaderTooltip =
    (ctgKey === "RSN" && rsnChnlBadgeTooltip) ? rsnChnlBadgeTooltip :
      (ctgKey === "LOCAL" && localChnlBadgeTooltip) ? localChnlBadgeTooltip :
        (packageData?.tooltip?.content) ? packageData?.tooltip : {};
  try {
    return (
    <DtvBox id={`ChnlListingPage-iconCtg-${ctgIndex}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "100%",
        minWidth: "100%",
        px: {
          xs: "14px",
          sm: "90px",
          md: "196px"
        },
        "& .MuiDivider-root": { width: "100% !important" }
    }}>
      {/** Category Header */}
      <DtvBox
        sx={{
          display: 'flex',
          justifyContent: "space-between",
          // flexing to columns at 800 since SVOD names need to wrap sooner than mobile
          [bps.down(800)]: {
            flexDirection: 'column'
          },
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
          position: "relative",
          paddingBottom: "8px"
        }}
      >
        <DtvBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            [bps.up(800)]: {
            maxWidth: "70%"
            },
            minHeight: {
 xs: "24px",
md: "28px"
}
          }}>
            {iconUrl && <>
              <DtvBox sx={{
 display: "flex",
height: "100%",
alignItems: "flex-start"
}}>
              <img
                alt="Genre Pack icon" src={iconUrl}
                style={{
                  width: "28px",
height: "28px",
marginRight: "4px",
                  ...theming?.mode === "light" && { filter: "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)" },
                  ...(ctgKey === "MYFREE_DTV" && {
 width: "110px",
height: "31px"
})
                }}
              />
              </DtvBox>
            </>}
            <DtvText
              variant="body2medium"
              content={
                ctgValue?.toUpperCase() === "SPORTS" ? "NATIONAL SPORTS" :
                packageData?.isGenreMiniPack === true ? `${ctgValue} | Mini Pack` :
                ctgValue
              }
              sx={{
                fontSize: {
 xs: "16px",
md: "20px"
},
                color: theming?.textColor,
                lineHeight: {
 xs: "20.8px",
md: "24px"
},
                letterSpacing: { md: "-0.48px" },
                fontWeight: 500,
                ...(ctgKey === "MYFREE_DTV" && { marginLeft: "25px" }),
                "& span": {
                  marginLeft: "12px",
                  color: `${theming?.textColor} !important`
                }
              }}
              tooltip={{
                closeOnScroll: true,
                ...ctgHeaderTooltip
              }}
            />
        </DtvBox>
        <CategoryHeaderCta
          ctaLabel={ctaLabel}
          ctaUrl={ctaUrl}
          mode={theming?.mode}
        />
      </DtvBox>

      {ctgKey === "MYHOMETEAM" && (
        <DtvBox sx={{
          marginLeft: "32px",
          // ensure the "Hide/Change" doesn't wrap at ~600-650px
          [`& #${zipCodeModuleProps.id}-header-ctnr`]: {
            marginRight: {
 sm: "-60px !important",
md: 0
}
          }
        }}>
          <ZipcodeModule { ...zipCodeModuleProps }/>
        </DtvBox>
      )}

      {/** Channel Icons */}
      <DtvBox className={"chnlCtg-icons"} id={ctgKey}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(auto-fit, 74px)",
            sm: "repeat(auto-fit, 72px)",
            md: "repeat(auto-fill, 104px)"
          },
          gap: {
            xs: "4.5px 3px",
            sm: "4.5px 3px",
            md: "6px 4px"
          },
          justifyContent: {
            xs: "center",
            sm: "center",
            md: "flex-start"
          },
          overflow: "hidden",
          width: '100%',
          maxHeight: {
            xs: showMoreKey ? "5000px" : "475px",
            sm: "5000px"
          },
          transition: "max-height 300ms ease-in-out 100ms"
      }}>
        {channels.map((channel, index) => {
          const normalizedCtgKey = ctgKey?.toLowerCase();
          const isMockIcon = channel?.isMock && channel?.isMock === true;
          const isRSNLocalChannel = Boolean(channel?.isLocal || channel?.isRsn) || ['rsn', 'local'].includes(channel?.category?.toLowerCase?.())
          const genreNationalData = genreNationals?.genreNationalMapping?.find(item => item.channel === channel?.name);
          const isFirstChannelWithBadge = index === 0 && (
            channel?.isNational?.[normalizedCtgKey] ||
            isRSNLocalChannel
          );
          return (
            <DtvBox id={`icon-container-${channel?.category}-${index}`}
              key={`Banner-${channel?.uuid}`}
              tabIndex={0}
              aria-label={!isMockIcon ?
                `Click the ${channel?.name || ctgValue} icon to view available packages` :
                "Channel icon loading..."
              }
              sx={{
                backgroundColor: "transparent",
                borderRadius: "50%",
                position: "relative",
                width: {
  xs: "74px !important",
sm: "72px !important",
md: "104px !important"
},
                height: {
 xs: "74px !important",
sm: "72px !important",
md: "104px !important"
},
                padding: "0px",
                margin: "0px",
                // boxShadow: `0px 2px 20px 0px rgba(0, 0, 0, 0.05)`,
                "&:focus-visible": {
                  outline: "2px solid #3885E1",
                  outlineOffset: "-2px"
                }
            }}>
              {(channel?.name &&
              <Tooltip
                describeChild
                content={channel?.name}
                disableCursorPointer={true}
                enterTouchDelay={0}
                placement={isRSNLocalChannel ? 'top' : 'bottom'}
                PopperProps={{
                  modifiers: [
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    phase: 'main'
                  }, {
                    name: 'flip',
                    enabled: true,
                    phase: 'main'
                  }, {
                    name: 'offset',
                    options: {
                      offset: [0, -20]
                    }
                  }],
                  sx: {
                    width: 'unset !important',
                    "& .MuiTypography-root": {
                      fontFamily: `roboto-regular`,
                      fontSize: "14px"
                    }
                  }
                }}
                sx={{
                  "& #popper[data-popper-escaped]": {
                    visibility: 'hidden'
                  }
                }}
                tooltipStyles={{
                  ...tooltipStyles.tooltip,
                  top: isFirstChannelWithBadge ? {
 xs: "23px",
sm: "0px"
} : "0px",
                  marginLeft: isFirstChannelWithBadge ? {
 xs: "78px",
sm: "0px"
} : "0px"
                }}>
                  <>
                    <MockIconBg />
                    <CardMedia
                      alt={channel?.name}
                      aria-labelledby={`icon-container-${channel?.category}-${index}`} component="img"
                      key={channel?.name}
                      // eslint-disable-next-line eqeqeq
                      loading={(ctgIndex == 1 || ctgIndex == 2) ? "eager" : "lazy"}
                      src={channel.icon}
                      sx={{
                        paddingTop: "1.3px", // eyeballed to center svg exactly
                        fontSize: "8px",
color: "#FFF",
                        fill: "#747474",
                        // contentVisibility: (ctgIndex !== 1 && ctgIndex !== 2) ? "auto" : "visible",
                        position: "relative",
                        zIndex: zIndexMap.channelIcon
                      }}
                      onClick={() => handleIconClick(channel)}
                      onError={e => e.currentTarget.parentElement.style.display = "none" }
                  />
                  {(channel?.isLocal || channel?.category?.toLowerCase?.() === 'local') && (
                    <LocalRsnTooltip
                      genreNationalLabel={genreNationalData?.nationalTooltipLabel || 'National'}
                      type={channel?.isNational?.[normalizedCtgKey] ? 'national' : 'local'}
                      tooltipProps={
                        channel?.isNational?.[normalizedCtgKey]
                          ? { content: genreNationalData?.nationaltooltipContent || ""}
                          : localChnlBadgeTooltip
                      }
                    />
                  )}
                    {(channel?.isRsn || channel?.category?.toLowerCase?.() === 'rsn') && (
                      <LocalRsnTooltip
                        tooltipProps={rsnChnlBadgeTooltip}
                        type='rsn'
                      />
                    )}
                  </>
              </Tooltip>
              )}
            </DtvBox>
          );
        })}
      </DtvBox>

      <ShowMoreButton
        ctgIndex={ctgIndex}
        ctgKey={ctgKey}
        handleShowMoreClick={handleShowMoreClick}
        mode={theming?.mode}
        showMoreKey={showMoreKey}
        textColor={theming?.textColor}
      />

      {/* MyHomeTeam Modal Cta */}
      {footerCta?.label && (
        <CategoryFooterCta footerCta={footerCta} mode={theming?.mode} />
      )}
      {/** Divider */}
      {!isLast ? (
        <Divider mode={theming?.mode} sx={{
          ...theming?.mode === "light" && { backgroundColor: "#D1D1D1 !important" },
          mt: {
 xs: "36px",
sm: "54px",
md: "60px"
},
          mb: {
 xs: "48px",
sm: "54px",
md: "60px"
}

        }}
        />
      ) : (
        <DtvBox sx={{
 mt: {
 xs: "36px",
sm: "48px",
md: "170px"
}
}} />
      )}
    </DtvBox>
    );
  } catch(error) {
    console.error("Error rendering IconCategory:", error);
    return <></>;
  }
};

export default React.memo(IconCategory);
